/**
 * `contacts.suppressions` — the per-contact refusals (design §1.4, §4.7).
 *
 * Three flags on one row, because they are one conversation with a person rather
 * than three features:
 *
 *  - `noRecording` — the do-not-record flag the recorder honours automatically.
 *    §4.3: the app "refuses to arm and says why". A manual "Record now" is
 *    refused too, because a flag a button overrides is advisory, and this one is
 *    not.
 *  - `noEnrichment` — GDPR erasure, honoured forever (§3.2's gate). Checked on
 *    the write path as well as the read, because a queued job can predate it.
 *  - `noOutreach` — Hermes never drafts to them.
 *
 * ## Why `remove` is a POST rather than a DELETE
 *
 * It is not a delete. `key_hash` is what makes an erasure survive the contact's
 * own deletion, so lifting a flag CLEARS it and keeps the row when there is
 * still something to remember. Modelling that as `DELETE /suppressions/{id}`
 * would promise a row disappearance the server must not perform — and the
 * contract already forbids a body on DELETE, which the per-flag payload needs.
 */

import { z } from 'zod';
import { isoDateTimeSchema, uuidSchema } from './common.ts';

export const SUPPRESSION_FLAGS = ['noRecording', 'noEnrichment', 'noOutreach'] as const;
export type SuppressionFlagName = (typeof SUPPRESSION_FLAGS)[number];
export const suppressionFlagSchema = z.enum(SUPPRESSION_FLAGS);

export const contactSuppressionSchema = z.object({
  id: uuidSchema,
  contactId: uuidSchema,
  /** Denormalised so the privacy screen renders without an N+1 over contacts. */
  contactName: z.string(),
  noRecording: z.boolean(),
  noEnrichment: z.boolean(),
  noOutreach: z.boolean(),
  reason: z.string().nullable(),
  createdAt: isoDateTimeSchema,
});

export const suppressionsListInputSchema = z.object({
  /** Narrow to one flag; the privacy screen's "do not record" section. */
  flag: suppressionFlagSchema.optional(),
  limit: z.int().min(1).max(500).default(200),
});

export const suppressionsListOutputSchema = z.object({
  items: z.array(contactSuppressionSchema),
});

/**
 * PATCH semantics: an absent flag is UNTOUCHED, not cleared.
 *
 * A privacy screen shows one checkbox per flag and may show only one of them;
 * under any other reading, a screen that renders "do not record" alone would
 * silently erase a GDPR suppression the user never saw.
 */
export const suppressionsCreateInputSchema = z.object({
  contactId: uuidSchema,
  noRecording: z.boolean().optional(),
  noEnrichment: z.boolean().optional(),
  noOutreach: z.boolean().optional(),
  reason: z.string().max(500).nullish(),
});

export const suppressionsRemoveInputSchema = z.object({
  contactId: uuidSchema,
  /** Flags to CLEAR. Omitting all of them clears every flag. */
  noRecording: z.boolean().optional(),
  noEnrichment: z.boolean().optional(),
  noOutreach: z.boolean().optional(),
});

export const suppressionsRemoveOutputSchema = z.object({
  ok: z.literal(true),
  contactId: uuidSchema,
  /** True when the row was removed outright: nothing left to remember. */
  deleted: z.boolean(),
  /** What survives — a cleared row still carrying an erasure hash, or null. */
  remaining: contactSuppressionSchema.nullable(),
});

export type ContactSuppression = z.output<typeof contactSuppressionSchema>;
export type SuppressionsListOutput = z.output<typeof suppressionsListOutputSchema>;
